import ContentCard, { ContentCardProps } from '@components/content/ContentCard'
import UIText from '@components/ui/UIText'
import { Box, Grid } from '@mui/joy'
import { ArticleListResponseVo } from '@schemas/non-auth'
import { AreaDiv } from '@services/api/Statistics/types'
import { theme } from '@ui/style'
import { pxToRem } from '@ui/style/muiTheme'
import { getRelativeTime } from '@utils/date'
import { getArticleLink } from '@utils/getLink'
import { uuidv4 } from '@utils/random'
import AsideArticlesItemSkeleton from '../AsideArticlesItemSkeleton'
import ArticleItem from './ArticleItem'
import { scrollToTopAndEnableAutoScrollRestoration } from '@utils/scroll'
import { usePathname } from 'next/navigation'

type AsideArticlesItemProps = {
	title: string
	hasRanking?: boolean
	data: ArticleListResponseVo[]
	isLoading?: boolean
	areaDiv?: AreaDiv
	areaIdx?: number
}

export const rankStyle = {
	fontSize: { lg: pxToRem(28), sm: pxToRem(24) },
	lineHeight: { lg: pxToRem(28), sm: pxToRem(26) },
	fontWeight: 600,
	color: theme.color.colMintBlue,
	minWidth: { xs: pxToRem(24) },
	flex: 'none',
}

export const cardProps: Omit<ContentCardProps, 'content'> = {
	layoutConfig: {
		flexDirection: 'row-reverse',
		layoutGap: pxToRem(16),
	},
	thumbnailConfig: {
		thumbWidth: { xl: pxToRem(132), sm: pxToRem(120) },
		thumbHeight: {
			xl: `${pxToRem(74)}`,
			sm: `${pxToRem(68)}`,
		},
		thumbSx: {
			display: { xl: 'block', lg: 'none', md: 'block' },
		},
		badgeInset: pxToRem(6),
	},
	infoConfig: {
		infoSx: {
			'.info-title': {
				fontSize: { lg: pxToRem(17), xs: pxToRem(16) },
				lineHeight: { lg: pxToRem(24), xs: pxToRem(22) },
			},
			'.info-additionalText': {
				fontSize: pxToRem(14),
				lineHeight: { lg: pxToRem(18), xs: pxToRem(16) },
			},
		},
	},
}

function isEven(n: number) {
	return n % 2 === 0
}

const AsideArticlesItem = ({
	title,
	hasRanking = false,
	data = [],
	isLoading,
	areaDiv,
	areaIdx,
}: AsideArticlesItemProps) => {
	const firstTwoArticles = Array.isArray(data) ? data.slice(0, 2) : []
	const nextTwoArticles = Array.isArray(data) ? data.slice(2, 4) : []

	const pathname = usePathname()
	const handleClick = () => {
		if (pathname.startsWith('/article')) {
			scrollToTopAndEnableAutoScrollRestoration()
		}
	}

	return (
		<Box
			display="flex"
			flexDirection="column"
			sx={{
				'&>*:first-child': {
					marginBottom: { xs: pxToRem(4), lg: 0 },
				},
				'&>*:not(:first-child)': {
					marginTop: { xs: pxToRem(12), lg: pxToRem(16) },
				},
			}}
		>
			<UIText level="TitleM1" sx={{ xs: { mb: pxToRem(4) }, lg: 0 }}>
				{title}
			</UIText>

			{isLoading && <AsideArticlesItemSkeleton hasRanking={hasRanking} />}

			{data.map((article, idx) => (
				<Box
					key={article.articleIdx}
					display={{ xs: 'flex', md: 'none', lg: 'flex' }}
					sx={{
						width: '100%',
						paddingTop: { md: pxToRem(15), sm: pxToRem(11) },
						borderTop: `1px solid ${theme.color.colGray3}`,
						'& > div': { width: '100%' },
						'&>*:not(:first-child)': {
							marginLeft: pxToRem(12),
						},
					}}
					className="articleListItem"
				>
					{hasRanking && <UIText sx={{ ...rankStyle }}>{idx + 1}</UIText>}
					<ContentCard
						content={{
							title: article.articleTitle,
							href: getArticleLink(article, areaDiv, areaIdx),
							src: article.vodInfo
								? article.vodInfo.vodThumbnailImgUrl
								: article.articleThumbnailImgUrl ?? '',
							alt: `${article.articleTitle} 썸네일 이미지`,
							additionalText: getRelativeTime(article.publicationDate) ?? '',
							duration: article?.vodInfo?.playTime ?? '',
							showVideoViewIcon: article.isVideoView,
							onClick: handleClick,
						}}
						layoutConfig={cardProps.layoutConfig}
						thumbnailConfig={{
							...cardProps.thumbnailConfig,
							isContain: article.articleThumbnailImageViewType !== 'WIDTH',
						}}
						infoConfig={cardProps.infoConfig}
					/>
				</Box>
			))}

			<Grid
				container
				columns={12}
				sx={{
					flexGrow: 1,
					borderTop: `1px solid ${theme.color.colGray3}`,
					display: { sm: 'none', md: 'inherit', lg: 'none' },
				}}
			>
				{[firstTwoArticles, nextTwoArticles].map((group, groupIdx) => (
					<Grid
						key={uuidv4()}
						xs={6}
						sx={{
							'.articleListItem:last-child': {
								border: isEven(groupIdx) ? 'none' : 'inherit',
								paddingBottom: isEven(groupIdx) ? 0 : 'inherit',
							},
							position: 'relative',
							'&::after': isEven(groupIdx)
								? {
										content: '""',
										display: 'block',
										height: 'calc(100% - 12px)',
										width: '1px',
										backgroundColor: theme.color.colGray3,
										position: 'absolute',
										right: pxToRem(-1),
										top: pxToRem(12),
								  }
								: { display: 'none' },
						}}
						pr={isEven(groupIdx) ? pxToRem(12) : 0}
						pl={isEven(groupIdx) ? 0 : pxToRem(12)}
						className="articleGroup_Test"
					>
						{group.map((article, idx) => (
							<ArticleItem
								key={article.articleIdx}
								article={article}
								index={groupIdx * 2 + idx}
								hasRanking={hasRanking}
								areaDiv={areaDiv}
								areaIdx={areaIdx}
							/>
						))}
					</Grid>
				))}
			</Grid>
		</Box>
	)
}

export default AsideArticlesItem
